import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries } from "lightweight-charts";
import api from "../lib/api";

const INTERVALS = ["1m", "5m", "15m", "1h", "4h", "1d"];

export default function Chart({ symbol, marketClosed, markPrice }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const volRef = useRef(null);
  const lastCandleRef = useRef(null);
  const loadedSymbolRef = useRef(null);
  const [interval, setInterval2] = useState("15m");
  const [ticker, setTicker] = useState(null);

  // init chart
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: { background: { color: "#111827" }, textColor: "#9CA3AF", fontFamily: "JetBrains Mono" },
      grid: { vertLines: { color: "#1F2937" }, horzLines: { color: "#1F2937" } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: "#1F2937" },
      timeScale: { borderColor: "#1F2937", timeVisible: true, secondsVisible: false },
      localization: { locale: "en-US" },
      autoSize: true,
    });
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: "#0ECB81", downColor: "#F6465D",
      borderUpColor: "#0ECB81", borderDownColor: "#F6465D",
      wickUpColor: "#0ECB81", wickDownColor: "#F6465D",
    });
    const vol = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "",
      color: "#26a69a",
    });
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });

    chartRef.current = chart;
    candleRef.current = candles;
    volRef.current = vol;

    return () => chart.remove();
  }, []);

  // load data
  useEffect(() => {
    if (!symbol || !candleRef.current) return;
    let alive = true;
    let isFirstLoad = true;
    // Invalidate any stale data/price sync from the previous pair until fresh klines load
    lastCandleRef.current = null;
    loadedSymbolRef.current = null;
    const load = async () => {
      try {
        const { data } = await api.get(`/market/klines?symbol=${symbol}&interval=${interval}&limit=300`);
        if (!alive || !data || data.length === 0) return;
        candleRef.current.setData(data.map((k) => ({ time: k.time, open: k.open, high: k.high, low: k.low, close: k.close })));
        volRef.current.setData(data.map((k) => ({ time: k.time, value: k.volume, color: k.close >= k.open ? "rgba(14,203,129,0.4)" : "rgba(246,70,93,0.4)" })));
        const last = data[data.length - 1];
        const first = data[0];
        lastCandleRef.current = { time: last.time, open: last.open, high: last.high, low: last.low, close: last.close };
        loadedSymbolRef.current = symbol;
        setTicker({ price: last.close, change: ((last.close - first.open) / first.open) * 100, high: Math.max(...data.map(d => d.high)), low: Math.min(...data.map(d => d.low)) });
        // On FIRST load for this symbol/interval — fit time scale + reset Y scale to candle range.
        if (isFirstLoad && chartRef.current && containerRef.current) {
          const w = containerRef.current.clientWidth;
          const h = containerRef.current.clientHeight;
          if (w > 0 && h > 0) chartRef.current.resize(w, h);
          // Reset Y axis to autoscale (critical when switching between BTC $74k → ETH $2k etc.)
          try {
            chartRef.current.priceScale("right").applyOptions({ autoScale: true });
          } catch { /* older versions */ }
          chartRef.current.timeScale().fitContent();
          isFirstLoad = false;
        }
      } catch { /* ignore */ }
    };
    load();
    const i = setInterval(load, 5000);
    return () => { alive = false; clearInterval(i); };
  }, [symbol, interval]);

  // Live-sync the last (forming) candle to the shared mark/last price so the
  // chart price always matches the order book and mark price.
  useEffect(() => {
    // Only sync when the loaded candles AND the price belong to the current pair
    if (!markPrice || !candleRef.current || !lastCandleRef.current || loadedSymbolRef.current !== symbol) return;
    const c = lastCandleRef.current;
    const updated = {
      time: c.time,
      open: c.open,
      high: Math.max(c.high, markPrice),
      low: Math.min(c.low, markPrice),
      close: markPrice,
    };
    lastCandleRef.current = updated;
    try { candleRef.current.update(updated); } catch { /* ignore */ }
    setTicker((t) => (t ? { ...t, price: markPrice } : t));
  }, [markPrice, symbol]);

  // Re-measure on window/container resize (e.g. mobile drawer close, orientation change)
  useEffect(() => {
    if (!chartRef.current || !containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      if (!chartRef.current) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) chartRef.current.resize(w, h);
    });
    ro.observe(el);
    const onOrientation = () => {
      setTimeout(() => {
        if (chartRef.current && el.clientWidth > 0) {
          chartRef.current.resize(el.clientWidth, el.clientHeight);
          chartRef.current.timeScale().fitContent();
        }
      }, 300);
    };
    window.addEventListener("orientationchange", onOrientation);
    window.addEventListener("resize", onOrientation);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", onOrientation);
      window.removeEventListener("resize", onOrientation);
    };
  }, []);

  return (
    <div className="h-full flex flex-col bg-[#111827]" data-testid="chart-container">
      <div className="flex items-center justify-between px-2 sm:px-4 py-2 border-b border-[#1F2937] gap-2 overflow-hidden">
        {/* Desktop-only: full info */}
        <div className="hidden lg:flex items-center gap-4 min-w-0">
          <h2 className="font-semibold text-lg" data-testid="chart-symbol">{symbol?.replace("USDT", "/USDT")}</h2>
          {marketClosed && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F6465D]/15 text-[#F6465D] ring-1 ring-inset ring-[#F6465D]/30 whitespace-nowrap" data-testid="market-closed-badge">
              Рынок закрыт
            </span>
          )}
          {ticker && (
            <>
              <span className={`mono text-lg font-semibold ${ticker.change >= 0 ? "text-[#0ECB81]" : "text-[#F6465D]"}`} data-testid="chart-price">
                ${(markPrice || ticker.price).toLocaleString("en-US", { maximumFractionDigits: (markPrice || ticker.price) < 1 ? 4 : 2 })}
              </span>
              <span className={`mono text-xs ${ticker.change >= 0 ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                {ticker.change >= 0 ? "+" : ""}{ticker.change.toFixed(2)}%
              </span>
              <div className="hidden xl:flex gap-3 text-xs text-[#9CA3AF]">
                <span>H <span className="text-white mono">{ticker.high.toFixed(2)}</span></span>
                <span>L <span className="text-white mono">{ticker.low.toFixed(2)}</span></span>
              </div>
            </>
          )}
        </div>
        {/* Mobile-only: H/L only (symbol+price already shown above) */}
        <div className="lg:hidden flex items-center gap-3 text-[10px] text-[#9CA3AF] min-w-0 truncate">
          {ticker && (
            <>
              <span>H <span className="text-white mono">{ticker.high.toLocaleString("en-US", { maximumFractionDigits: ticker.high < 1 ? 4 : 0 })}</span></span>
              <span>L <span className="text-white mono">{ticker.low.toLocaleString("en-US", { maximumFractionDigits: ticker.low < 1 ? 4 : 0 })}</span></span>
            </>
          )}
        </div>
        <div className="flex gap-0.5 sm:gap-1 shrink-0">
          {INTERVALS.map((iv) => (
            <button key={iv} onClick={() => setInterval2(iv)}
              className={`px-1.5 sm:px-2 py-1 text-[11px] sm:text-xs rounded-sm transition-colors ${interval === iv ? "bg-[#1F2937] text-[#FCD535]" : "text-[#9CA3AF] hover:text-white"}`}
              data-testid={`interval-${iv}`}>
              {iv}
            </button>
          ))}
        </div>
      </div>
      <div ref={containerRef} className="flex-1" />
    </div>
  );
}
